import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Check, Loader } from 'lucide-react';

/**
 * One-time treatments and extras a customer can ask for — a weed treatment, a
 * feed, a bush trim — priced off the cost sheet for their own yard where the
 * sheet can price it (owner, Sep 17 2026).
 *
 * Asking sends it to the office. Nothing is booked or charged here; the office
 * puts it on their next visit once it is approved.
 */
type Api = { get: (u: string, c?: any) => Promise<any>; post: (u: string, b?: any) => Promise<any> };

export default function OneTimeExtras({ api, token, propertyId }: { api: Api; token?: string | null; propertyId?: number | null }) {
  const [asked, setAsked] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const { data } = useQuery(['plan-extras', token || propertyId || 'public'],
    () => api.get('/plans/extras', { params: { ...(token ? { token } : {}) } }).then((r: any) => r.data));

  const extras: any[] = data?.extras || [];
  if (!extras.length) return null;

  async function ask(key: string) {
    setBusy(key);
    try {
      const { data: out } = await api.post('/plans/request', { kind: 'addon', addon_key: key, ...(token ? { token } : {}) });
      setAsked(a => ({ ...a, [key]: out.message || 'Asked, we will confirm.' }));
    } catch (e: any) {
      setAsked(a => ({ ...a, [key]: e?.response?.data?.message || 'That did not send. Text us instead.' }));
    } finally { setBusy(null); }
  }

  return (
    <section className="rounded-3xl border border-brand-100 bg-white shadow-soft p-5">
      <h2 className="text-base font-black uppercase tracking-tight text-gray-900">One-off jobs</h2>
      <p className="text-[14px] text-gray-700 mb-3">
        {token ? 'We add these to your next visit, so there is no extra trip.' : 'Ask for one of these on its own, no plan needed.'}
      </p>
      <ul className="divide-y divide-brand-50">
        {extras.map(e => (
          <li key={e.key} className="py-2.5 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-bold text-gray-900">{e.name}</p>
              {e.blurb && <p className="text-[14px] text-gray-700">{e.blurb}</p>}
              {asked[e.key] && <p className="text-xs text-emerald-700 mt-0.5 flex items-center gap-1"><Check className="h-3 w-3" /> {asked[e.key]}</p>}
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-[16px] font-black text-brand-700 tabular-nums">${Number(e.price || 0).toFixed(0)}</p>
              {token ? (
                <button onClick={() => ask(e.key)} disabled={!!busy || !!asked[e.key]}
                  className="press mt-1 text-[14px] font-bold text-brand-700 underline min-h-[44px] disabled:text-gray-500">
                  {busy === e.key ? <Loader className="h-3 w-3 animate-spin inline" /> : asked[e.key] ? 'Asked' : 'Ask for this'}
                </button>
              ) : (
                <p className="text-[13px] text-gray-600 mt-1">text us</p>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-gray-500 mt-2">Prices are for your yard, plus tax. Nothing is charged until the work is done.</p>
    </section>
  );
}
